import { add, format, isValid, parseISO } from "date-fns";
import { isoFormat } from "../../../../../common/helpers/formatDateTime";
import { YesNoOptions } from "../../../../customer/contract/constants";
import {
  ChangeFringeBenefits,
  FringeBenefit,
  FringeBenefitApiType,
  FringeBenefitDetails,
  FringeBenefitFormValues,
  FringeBenefitTypeField,
} from "./types";

export const parseFormDate = (dateString?: string | null): Date | null => {
  if (!dateString) return null;
  const date = parseISO(dateString);
  return isValid(date) ? date : null;
};

export const getFringeBenefitFormDefaultValues = (
  fringeBenefit: FringeBenefit | null,
): FringeBenefitFormValues => ({
  id: fringeBenefit?.id || "",
  expenseTypeCode: fringeBenefit?.expenseTypeCode || "",
  details: { ...fringeBenefit?.details },
  description: fringeBenefit?.description || "",
  additionalInformation: fringeBenefit?.additionalInformation || "",
  startDate: parseFormDate(fringeBenefit?.startDate),
  endDate: parseFormDate(fringeBenefit?.endDate),
  amount: fringeBenefit?.amount || "",
  includedInTotalSalary: fringeBenefit?.includedInTotalSalary
    ? YesNoOptions.Yes
    : YesNoOptions.No,
});

// Backend only accepts the detail keys declared for the selected expense type
const pickDetails = (
  details: FringeBenefitDetails,
  fields: FringeBenefitTypeField[],
): FringeBenefitDetails | null => {
  const entries = fields
    .map((field) => [field.name, details[field.name]?.trim() ?? ""])
    .filter(([, value]) => !!value);
  return entries.length ? Object.fromEntries(entries) : null;
};

export const formatFringeBenefitDetails = (
  details: FringeBenefitDetails | null,
  fields: FringeBenefitTypeField[],
): string =>
  fields
    .filter((field) => !!details?.[field.name])
    .map((field) => `${field.label}: ${details?.[field.name]}`)
    .join(", ");

// For UI, map form values to table item
export const mapFormValuesToFringeBenefit = (
  values: FringeBenefitFormValues,
  fields: FringeBenefitTypeField[] = [],
): FringeBenefit => {
  return {
    id: values.id,
    expenseTypeCode: values.expenseTypeCode,
    description: values.description.trim(),
    details: pickDetails(values.details, fields),
    additionalInformation: values.additionalInformation,
    startDate: values.startDate ? format(values.startDate, isoFormat) : "",
    // Empty end date means the benefit is open-ended
    endDate: values.endDate ? format(values.endDate, isoFormat) : "",
    amount: values.amount,
    includedInTotalSalary: values.includedInTotalSalary === YesNoOptions.Yes,
  };
};

// Expense type and start date are unique together, and they stay with the row
// when a new row is sorted into the list.
const fringeBenefitRowId = (expenseTypeCode: string, from: string) =>
  `${expenseTypeCode}|${from}`;

// For UI, display incoming API data in a table
export const mapApiTypesToFringeBenefits = (
  apiTypes: FringeBenefitApiType[],
): FringeBenefit[] =>
  apiTypes.map((apiType) => ({
    id: fringeBenefitRowId(apiType.expenseTypeCode, apiType.from),
    expenseTypeCode: apiType.expenseTypeCode,
    description: apiType.description,
    additionalInformation: apiType.additionalInformation,
    details: apiType.details || null,
    startDate: apiType.from,
    endDate: apiType.to ?? "",
    amount: apiType.value,
    includedInTotalSalary: apiType.includedInTotalSalary ?? false,
  }));

// For API, map one fringe benefit data for saving
export const mapFringeBenefitToApiType = (
  fringeBenefit: FringeBenefit,
): FringeBenefitApiType => ({
  from: fringeBenefit.startDate,
  to: fringeBenefit.endDate || null,
  expenseTypeCode: fringeBenefit.expenseTypeCode,
  value: fringeBenefit.amount,
  description: fringeBenefit.description,
  additionalInformation: fringeBenefit.additionalInformation,
  details: fringeBenefit.details,
  includedInTotalSalary: fringeBenefit.includedInTotalSalary,
});

const dayBefore = (isoDate: string) =>
  format(add(parseISO(isoDate), { days: -1 }), isoFormat);

// For API, map all fringe benefits for saving. Backend requires every row that
// is followed by another row of the same type to end before that row starts,
// so a missing or overlapping end date is cut to the day before the next row.
export const mapFringeBenefitsToApiTypes = (
  fringeBenefits: FringeBenefit[],
): FringeBenefitApiType[] => {
  const sorted = [...fringeBenefits].sort((a, b) =>
    a.startDate.localeCompare(b.startDate),
  );

  return sorted
    .map((benefit, index) => {
      const apiType = mapFringeBenefitToApiType(benefit);
      const nextOfSameType = sorted
        .slice(index + 1)
        .find((other) => other.expenseTypeCode === benefit.expenseTypeCode);
      if (!nextOfSameType) return apiType;

      const lastAllowedEnd = dayBefore(nextOfSameType.startDate);
      return {
        ...apiType,
        to:
          benefit.endDate && benefit.endDate < lastAllowedEnd
            ? benefit.endDate
            : lastAllowedEnd,
      };
    })
    .sort(
      (a, b) =>
        a.from.localeCompare(b.from) ||
        a.expenseTypeCode.localeCompare(b.expenseTypeCode),
    );
};

// For API, construct payload for saving all fringe benefits
export const constructFringeBenefitPayload = (
  updated: FringeBenefit,
  existingFringeBenefits: FringeBenefit[],
  employmentId: string,
  version: number,
): ChangeFringeBenefits => {
  const fringeBenefits = updated.id
    ? existingFringeBenefits.map((benefit) =>
        benefit.id === updated.id ? updated : benefit,
      )
    : [...existingFringeBenefits, updated];
  return {
    employmentId,
    version,
    settings: mapFringeBenefitsToApiTypes(fringeBenefits),
  };
};
